import {
  FC, useEffect, useState,
} from 'react';
import Moment from 'react-moment';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Checkbox,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import STRING_KEYS from '../../../language/keys';
import {
  DATE_FORMAT, MATCH_STATUS_MAP, ENGAGEMENT_STATUS_MAP,
} from '../../../consts';
import { MATCH_PARAMS } from '../../../consts/requestParamsKeys';
import { Sort } from '../../../interfaces/apiRequest.interface';
import TableHeadContainer from '../../../components/Table/TableHead';
import TableContainer from '../../../components/Table/TableContainer';
import TableCountRow from '../../../components/Table/TableCountRow';
import PositionDetailsAction from '../PositionDetailsAction';
import useNavigate from '../../../hooks/useNavigate';
import Status from '../../../components/Status';
import { MatchStatusEnum } from '../../../consts/enums';
import { CustomTooltip } from '../../../components/CustomTooltip';
import { useUpdateMatches } from '../../../hooks/usePositions';
import { MatchesBulkPanel } from '../AppMatchesTab/MatchesBulkPanel';
import style from './style';

interface PositionDetailsTableProps {
  accountId: string,
  positionId: string,
  matches: Match[];
  onPageChange: (newPageNumber: number) => void
  page: number;
  totalCount: number;
  isLoading: boolean;
  order: Sort;
  orderHandler: (val: Sort) => void,
  refetchMatches: () => void,
  setEnableQuery: (value: boolean) => void,
}

const PositionDetailsTable: FC<PositionDetailsTableProps> = (props) => {
  const {
    accountId,
    positionId,
    matches,
    onPageChange,
    page,
    totalCount,
    isLoading,
    order,
    orderHandler,
    refetchMatches,
    setEnableQuery,
  } = props;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (selectedIds.length) {
      setEnableQuery(false);
    } else {
      setEnableQuery(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const updateMatchesStatus = useUpdateMatches(refetchMatches);

  const checkSelectAll = () => {
    return matches.every((match) => {
      return selectedIds.includes(match.id);
    });
  };

  const isAllSelected = checkSelectAll();

  const unselectIds = (id: string[]) => {
    setSelectedIds((prev) => {
      return prev.filter((selectedId) => !id.includes(selectedId));
    });
  };

  const handleResetSelectAll = () => {
    setSelectedIds([]);
  };

  const handleSelectToggleAll = () => {
    setSelectedIds(() => {
      if (isAllSelected) {
        return [];
      }

      return matches.map((match) => match.id);
    });
  };

  const handleAcceptClick = (ids: string[], status: MatchStatusEnum) => {
    updateMatchesStatus.mutateAsync({
      accountId,
      positionId,
      ids,
      status,
    }).then(() => {
      unselectIds(ids);
    });
  };

  const { goToMatchView, goToMatchEditWithModalProp, goToMatchEdit } = useNavigate();

  const headers = [
    {
      key: MATCH_PARAMS.FIRST_NAME,
      value: translate(STRING_KEYS.POSITIONS_DETAILS_TABLE_HEADER_TITLE),
      isSortable: true,
    },
    {
      key: MATCH_PARAMS.JOB_TITLE,
      value: translate(STRING_KEYS.POSITIONS_DETAILS_TABLE_HEADER_POSITION),
      isSortable: true,
    },
    {
      key: MATCH_PARAMS.STATUS,
      value: translate(STRING_KEYS.POSITIONS_DETAILS_TABLE_HEADER_MATCH),
      isSortable: true,
    },
    {
      key: MATCH_PARAMS.ENGAGEMENT_STATUS,
      value: translate(STRING_KEYS.POSITIONS_DETAILS_TABLE_HEADER_ENGAGEMENT),
      isSortable: true,
    },
    {
      key: MATCH_PARAMS.CREATED_AT,
      value: translate(STRING_KEYS.POSITIONS_DETAILS_TABLE_HEADER_DATE),
      isSortable: true,
    },
    {
      key: 'match_action',
      value: '',
    },
  ];
  const isSelected = (id: string) => {
    return selectedIds.some((selectedId: string) => id === selectedId);
  };

  const onSelectClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    event.stopPropagation();
    setSelectedIds((prev: string[]) => {
      const arr = [...prev];
      const idx = arr.indexOf(id);
      if (idx === -1) {
        arr.push(id);
      } else {
        arr.splice(idx, 1);
      }

      return arr;
    });
  };

  const renderMatchEngagementStatus = (match: Match) => {
    if (match.status !== MatchStatusEnum.published) {
      return '';
    }

    const engageStatus = translate(STRING_KEYS.ENGAGEMENT_STATUS[match.engagementStatus]);

    const statusChip = (
      <Status
        label={ engageStatus }
        type={ ENGAGEMENT_STATUS_MAP[match.engagementStatus] }
      />
    );

    return statusChip;
  };

  const renderTableBody = () => {
    const moveToEditMatch = (matchId: string) => {
      goToMatchEdit(positionId, accountId, matchId);
    };
    const moveToViewMatch = (matchId: string) => {
      goToMatchView(positionId, accountId, matchId);
    };
    const moveToEditMatchAndOpenModal = (matchId: string) => {
      goToMatchEditWithModalProp(positionId, accountId, matchId, true);
    };

    return matches.map((match) => {
      const status = translate(STRING_KEYS.MATCH_STATUSES[match.status]);

      return (
        <TableRow
          hover
          key={ match.id }
          onClick={ () => {
            goToMatchView(positionId, accountId, match.id);
          } }
        >
          <TableCell>
            <Checkbox
              checked={ isSelected(match.id) }
              onClick={ (event) => onSelectClick(event, match.id) }
            />
          </TableCell>
          <TableCell>
            <Box sx={ style.box.sx }>
              <Avatar
                src={ match.profileImage }
                variant={ style.avatar.variant }
                sx={ style.avatar.sx }
              />
              <Box sx={ style.rowBox.sx }>
                <CustomTooltip title={ `${match.firstName} ${match.lastName}` }>
                  <Typography
                    noWrap
                    variant={ style.rowBox.textFirst.variant }
                  >
                    {match.firstName} {match.lastName}
                  </Typography>
                </CustomTooltip>
                <CustomTooltip title={ match.id }>
                  <Typography
                    noWrap
                    color={ style.rowBox.textSecond.color }
                    variant={ style.rowBox.textSecond.variant }
                  >
                    {match.id}
                  </Typography>
                </CustomTooltip>
              </Box>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={ style.rowBox.sx }>
              <CustomTooltip title={ match.currentJobTitle }>
                <Typography
                  noWrap
                  variant={ style.rowBox.textFirst.variant }
                >
                  {match.currentJobTitle}
                </Typography>
              </CustomTooltip>
              <CustomTooltip title={ match.currentCompanyName }>
                <Typography
                  noWrap
                  color={ style.rowBox.textSecond.color }
                  variant={ style.rowBox.textSecond.variant }
                >
                  {match.currentCompanyName}
                </Typography>
              </CustomTooltip>
            </Box>
          </TableCell>
          <TableCell>
            <Status
              label={ status }
              type={ MATCH_STATUS_MAP[match.status] }
              isLockedIcon={ match.isLocked }
            />
          </TableCell>
          <TableCell>
            { renderMatchEngagementStatus(match) }
          </TableCell>
          <TableCell>
            <Moment format={ DATE_FORMAT.TABLE_LIST }>
              {match.createdAt}
            </Moment>
          </TableCell>
          <TableCell>
            <PositionDetailsAction
              id={ match.id }
              moveToViewMatch={ moveToViewMatch }
              moveToEditMatch={ moveToEditMatch }
              moveToEditMatchAndOpenModal={ moveToEditMatchAndOpenModal }
            />
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <TableContainer
      totalCount={ totalCount }
      onPageChange={ (e, newPage) => {
        onPageChange(newPage);
      } }
      page={ page }
      isLoading={ isLoading }
    >
      <TableHeadContainer
        headers={ headers }
        order={ order }
        orderHandler={ orderHandler }
        hasSelectAll
        onSelectAll={ handleSelectToggleAll }
        isAllSelected={ isAllSelected && matches.length !== 0 }
      />
      <TableBody>
        <TableCountRow
          count={ totalCount }
          colSpan={ headers.length }
          text={ translate(STRING_KEYS.MATCH_TABLE_ROW_RESULT_COUNT) }
        />
        { renderTableBody() }
      </TableBody>
      <>
        {selectedIds.length !== 0 && (
          <MatchesBulkPanel counter={ selectedIds.length }
            onResetSelectedIds={ handleResetSelectAll }
            onAcceptClick={ (status: MatchStatusEnum) => handleAcceptClick(selectedIds, status) }
          />
        )}
      </>
    </TableContainer>
  );
};

export default PositionDetailsTable;
